import { Button } from "@/components/Base/Button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { useStore } from "@/stores/useStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PatchCardDto } from "@/types/v2";
import { TagsField } from "@/components/Common/form/TagsField";
import { SHOW_ALL_SUGGESTED_TAGS } from "@/lib/const";
import { sortTagsByShopOrder } from "@/lib/tags";

const formSchema = z.object({
  tags: z.array(z.string()).optional(),
});

type CardTagsFormValues = {
  tags: string[];
};

export interface CardTagsFormProps {
  cancelButton?: ReactNode;
  submitButton?: ReactNode;
  cardData?: Pick<PatchCardDto, "tags">;
  onSubmit: (values: { tags: string[] }) => Promise<void>;
  className?: string;
}

export const CardTagsForm = ({
  cancelButton,
  submitButton,
  cardData,
  onSubmit,
  className,
}: CardTagsFormProps) => {
  const { tags } = useStore((s) => s);

  const formDefaultValues: CardTagsFormValues = {
    tags: sortTagsByShopOrder(cardData?.tags || [], tags?.tags),
  };

  const form = useForm<CardTagsFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues,
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(async (data) => {
          await onSubmit({ tags: data.tags });
        })}
        className={`space-y-4 ${className}`}
      >
        <FormField
          control={form.control}
          name="tags"
          render={() => (
            <FormItem>
              <FormLabel>Tags</FormLabel>
              <FormControl>
                <TagsField
                  form={form}
                  name="tags"
                  placeholder="Enter tags to be associated with this card"
                  suggestedTags={tags?.tags?.map((t) => t.label)}
                  maxTags={20}
                  maxSuggestedTags={SHOW_ALL_SUGGESTED_TAGS ? undefined : 20}
                  allTags={tags?.tags}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-row justify-end gap-4">
          {cancelButton ? cancelButton : null}
          {submitButton ? (
            submitButton
          ) : (
            <Button
              variant="primary-alt"
              type="submit"
              disabled={form.formState.isSubmitting}
            >
              {form.formState.isSubmitting ? "Saving..." : "Save Tags"}
            </Button>
          )}
        </div>
      </form>
    </Form>
  );
};
